import type { Project } from '../domain/schemas';
import { nowIso } from '../lib/ids';
import { Observable } from '../lib/store';
import { StorageError, toStorageError } from '../storage/errors';
import type { ProjectRepository } from '../storage/projectRepository';
import type { LockOutcome, ProjectLock } from './projectLock';

/**
 * Cross-tab protection of the open project: `exclusive` (this tab holds the Web Lock),
 * `unprotected` (no lock provider, or the browser has no Web Locks API: other tabs are not
 * detected), `lost` (the lock was taken away; saving stops), `none` (not open).
 */
export type EditingLock = 'none' | 'exclusive' | 'unprotected' | 'lost';

export type SaveStatus = 'loading' | 'saved' | 'unsaved' | 'saving' | 'error';

export interface SessionState {
  project: Project | null;
  status: SaveStatus;
  /** Error from the last failed save (data is still held in memory). */
  saveError?: string;
  /** Error that prevented the project from loading at all. */
  loadError?: StorageError;
  lastSavedAt?: string;
}

/**
 * Holds the open project in memory and persists it with a debounced autosave.
 * Edits made while a save is in flight are never lost: they trigger another save.
 * A failed save keeps the in-memory edits and reports an explicit error status.
 */
const LOCK_LOST = 'This tab lost its editing lock for the project, so changes are no longer saved here (another tab may be editing it). Download a backup of your changes, then reload.';

export class ProjectSession {
  readonly state = new Observable<SessionState>({ project: null, status: 'loading' });
  private revision = 0;
  private savedRevision = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<boolean> | null = null;
  private disposed = false;
  private readonly repo: ProjectRepository;
  private readonly projectId: string;
  private readonly debounceMs: number;
  private readonly lock: ProjectLock | null;
  private release: (() => void) | null = null;
  private acquiring: Promise<LockOutcome> | null = null;
  private lockState: EditingLock = 'none';
  private loads = 0;

  /** `lock` makes the session the project's only editor across tabs (see projectLock.ts). */
  constructor(repo: ProjectRepository, projectId: string, debounceMs = 700, lock: ProjectLock | null = null) {
    this.repo = repo;
    this.projectId = projectId;
    this.debounceMs = debounceMs;
    this.lock = lock;
  }

  get snapshot(): SessionState {
    return this.state.get();
  }

  /** Whether the open project is protected against edits from other tabs. */
  get editingLock(): EditingLock {
    return this.lockState;
  }

  private onLockLost(): void {
    if (this.lockState !== 'exclusive') return;
    this.lockState = 'lost';
    this.release = null;
    this.clearTimer();
    if (this.snapshot.project) {
      this.state.update({ status: 'error', saveError: LOCK_LOST });
    }
  }

  get hasUnsavedChanges(): boolean {
    return this.revision !== this.savedRevision;
  }

  async load(): Promise<void> {
    // Re-entrant: React StrictMode (development) disposes and loads the same session again.
    const generation = ++this.loads;
    this.disposed = false;
    this.state.set({ project: null, status: 'loading' });
    try {
      if (!this.lock) this.lockState = 'unprotected';
      else if (!this.release && this.lockState !== 'unprotected') {
        this.acquiring ??= this.lock(this.projectId, () => this.onLockLost());
        const outcome = await this.acquiring;
        if (generation !== this.loads) return; // a newer load() owns the acquisition
        if (this.disposed) {
          if (outcome.kind === 'acquired') outcome.release();
          this.acquiring = null;
          return;
        }
        if (outcome.kind === 'held-elsewhere' || outcome.kind === 'failed') {
          this.acquiring = null; // "Try again" asks again
          this.state.set({
            project: null,
            status: 'error',
            loadError:
              outcome.kind === 'held-elsewhere'
                ? new StorageError('conflict', 'This project is already open in another tab or window.', [
                    'A project can be edited in one place at a time, so one tab never overwrites the other’s changes. Close it there, then try again.',
                  ])
                : new StorageError(
                    'lock-failed',
                    'SonicForge could not make sure this project is not open in another tab, so it was not opened.',
                    [`The browser’s lock service failed${outcome.error instanceof Error && outcome.error.message ? ` (${outcome.error.message})` : ''}. Try again; if it keeps failing, reload the page.`],
                    { cause: outcome.error },
                  ),
          });
          return;
        }
        if (outcome.kind === 'acquired') {
          this.release = outcome.release;
          this.lockState = 'exclusive';
        } else this.lockState = 'unprotected';
      }
      const project = await this.repo.get(this.projectId);
      if (this.disposed || generation !== this.loads) return;
      this.state.set({ project, status: 'saved', lastSavedAt: project.updatedAt });
    } catch (error) {
      if (this.disposed || generation !== this.loads) return;
      this.state.set({ project: null, status: 'error', loadError: toStorageError(error, 'Could not load the project.') });
    }
  }

  /** Applies a pure transformation to the project and schedules an autosave. */
  update(mutate: (project: Project) => Project): void {
    const current = this.snapshot.project;
    if (!current) return;
    const next = mutate(current);
    if (next === current) return;
    this.revision++;
    this.state.update({ project: next, status: 'unsaved' });
    this.schedule();
  }

  /** Saves immediately. Resolves true when everything in memory is persisted. */
  async saveNow(): Promise<boolean> {
    this.clearTimer();
    if (this.inFlight) {
      await this.inFlight;
      if (!this.hasUnsavedChanges) return true;
    }
    if (!this.hasUnsavedChanges && this.snapshot.status !== 'error') return true;
    this.inFlight = this.persist();
    try {
      return await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }

  /**
   * Stops timers and flushes pending edits before the session is dropped. Resolves true
   * when everything in memory is persisted; false means the edits are still only in
   * `snapshot.project` (the caller must offer them to the user, never drop them).
   */
  async dispose(): Promise<boolean> {
    this.clearTimer();
    const generation = this.loads;
    this.disposed = true;
    let saved = true;
    if (this.hasUnsavedChanges && this.snapshot.project) saved = await this.saveNow().catch(() => false);
    // load() ran again meanwhile (React StrictMode in development): the session lives on.
    if (generation !== this.loads) return saved;
    // Release the editing lock only after the final save, so another tab never opens stale
    // data. A lock still being acquired is released by the load() that asked for it.
    if (this.release) {
      this.release();
      this.release = null;
      this.acquiring = null;
    }
    this.lockState = 'none';
    return saved;
  }

  private schedule(): void {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.saveNow();
    }, this.debounceMs);
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private async persist(): Promise<boolean> {
    const project = this.snapshot.project;
    if (!project) return false;
    // Never write without the editing lock once it was lost: another tab may own the project now.
    if (this.lockState === 'lost') {
      this.state.update({ status: 'error', saveError: LOCK_LOST });
      return false;
    }
    const revision = this.revision;
    this.state.update({ status: 'saving', saveError: undefined });
    try {
      const saved = await this.repo.save({ ...project, updatedAt: nowIso() });
      this.savedRevision = revision;
      const current = this.snapshot.project;
      const upToDate = this.revision === revision;
      this.state.update({
        project: current ? { ...current, updatedAt: saved.updatedAt } : current,
        status: upToDate ? 'saved' : 'unsaved',
        lastSavedAt: saved.updatedAt,
      });
      if (!upToDate && !this.disposed) this.schedule();
      return upToDate;
    } catch (error) {
      const storageError = toStorageError(error, 'Saving failed.');
      const detail = storageError.details.length ? ` ${storageError.details.join('; ')}` : '';
      this.state.update({ status: 'error', saveError: `${storageError.message}${detail}` });
      return false;
    }
  }
}

export { StorageError };
